import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { Helper } from '../../components/ui/Helper';
import { fmtEur, fmtDate } from '../../lib/utils';
// Pagina pubblica (no auth) — non usa apiFetch ma riusa la base URL versionata.
import { API_BASE_URL as API_BASE } from '../../lib/api';

/**
 * Pagina pubblica per la firma elettronica semplice di un preventivo.
 * URL: /sign/quotes/:token (no auth, accessibile a chiunque abbia il link).
 *
 * Flusso:
 *  1. Fetch dati pubblici del preventivo via GET /sign/quotes/:token
 *  2. Mostra contenuto (intro, righe, totali, T&C)
 *  3. L'utente firma con canvas, dichiara di accettare T&C
 *  4. POST /sign/quotes/:token/sign → registra firma + hash + IP/UA
 *  5. Mostra "Ricevuta firma" stampabile
 */

interface PublicQuote {
  id: string;
  number: string | null;
  title: string | null;
  introMessage: string | null;
  termsAndConditions: string | null;
  issueDate: string | null;
  validUntil: string | null;
  subtotal: string;
  vat: string;
  total: string;
  status: string;
  signedAt: string | null;
  rejectedAt: string | null;
  signerName: string | null;
  client: {
    legalName: string;
    addressLine?: string;
    city?: string;
    zip?: string;
    province?: string;
    vat?: string;
    taxCode?: string;
  };
  organization: { name: string; logoUrl: string | null } | null;
  lines: Array<{ description: string; quantity: number; unitPrice: number; vatRate: number }>;
}


export function QuoteSignPage() {
  const { token } = useParams<{ token: string }>();
  const [q, setQ] = useState<PublicQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [showSignBox, setShowSignBox] = useState(false);
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [receipt, setReceipt] = useState<{ documentHash: string; timestamp: string } | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Fetch
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/sign/quotes/${token}`)
      .then(async (r) => {
        if (!r.ok) throw new Error('Preventivo non trovato o link non valido');
        return r.json();
      })
      .then((data) => setQ(data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  // Signature canvas init
  useEffect(() => {
    if (!showSignBox || !canvasRef.current) return;
    const cv = canvasRef.current;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    // High-DPI
    const ratio = window.devicePixelRatio || 1;
    const rect = cv.getBoundingClientRect();
    cv.width = rect.width * ratio;
    cv.height = rect.height * ratio;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1a1a1a';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);

    let drawing = false;
    let lastX = 0, lastY = 0;
    const point = (e: PointerEvent) => {
      const r = cv.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const start = (e: PointerEvent) => { drawing = true; const p = point(e); lastX = p.x; lastY = p.y; };
    const move = (e: PointerEvent) => {
      if (!drawing) return;
      const p = point(e);
      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      lastX = p.x; lastY = p.y;
    };
    const end = () => { drawing = false; };

    cv.addEventListener('pointerdown', start);
    cv.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    cv.addEventListener('pointerleave', end);
    return () => {
      cv.removeEventListener('pointerdown', start);
      cv.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      cv.removeEventListener('pointerleave', end);
    };
  }, [showSignBox]);

  const clearCanvas = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    const rect = cv.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, []);

  const canvasIsBlank = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return true;
    const ctx = cv.getContext('2d');
    if (!ctx) return true;
    // Veloce: campiona alcuni punti, se non bianco → non blank
    const sample = ctx.getImageData(0, 0, cv.width, cv.height).data;
    for (let i = 0; i < sample.length; i += 400) {
      if (sample[i] !== 255 || sample[i + 1] !== 255 || sample[i + 2] !== 255) return false;
    }
    return true;
  }, []);

  const handleSign = async () => {
    if (!q || !token) return;
    if (!signerName.trim()) return alert('Inserisci il tuo nome completo');
    if (!acceptTerms) return alert('Devi accettare termini e condizioni per firmare');
    if (canvasIsBlank()) return alert('Disegna la tua firma nello spazio');

    setSigning(true);
    try {
      const cv = canvasRef.current!;
      const dataUrl = cv.toDataURL('image/png');
      const res = await fetch(`${API_BASE}/sign/quotes/${token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signerName: signerName.trim(),
          signerEmail: signerEmail.trim() || undefined,
          signatureDataUrl: dataUrl,
          acceptTerms: true,
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result?.message ?? 'Errore firma');
      setReceipt({ documentHash: result.receipt.documentHash, timestamp: result.receipt.timestamp });
      setQ({ ...q, status: 'ACCETTATO', signedAt: new Date().toISOString(), signerName: signerName.trim() });
      setShowSignBox(false);
    } catch (e: any) {
      alert(`Errore: ${e.message}`);
    } finally {
      setSigning(false);
    }
  };

  const handleReject = async () => {
    if (!q || !token) return;
    if (!confirm('Rifiutare questo preventivo? Questa azione non è reversibile.')) return;
    setSigning(true);
    try {
      const res = await fetch(`${API_BASE}/sign/quotes/${token}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason.trim() || undefined }),
      });
      if (!res.ok) throw new Error('Errore rifiuto');
      setQ({ ...q, status: 'RIFIUTATO', rejectedAt: new Date().toISOString() });
      setShowRejectBox(false);
    } catch (e: any) {
      alert(`Errore: ${e.message}`);
    } finally {
      setSigning(false);
    }
  };

  if (loading) return <SignPageShell><div className="py-20 text-center mute">Caricamento…</div></SignPageShell>;
  if (error || !q) return (
    <SignPageShell>
      <div className="py-20 text-center">
        <Icon name="alert" size={32} className="mute mb-4 mx-auto" />
        <h1 className="t-h1">Link non valido</h1>
        <div className="mute mt-2">Il link al preventivo non è valido o è scaduto.</div>
      </div>
    </SignPageShell>
  );

  const isSigned = q.status === 'ACCETTATO' || q.status === 'CONVERTITO';
  const isRejected = q.status === 'RIFIUTATO' || q.status === 'SCADUTO';
  const isActionable = !isSigned && !isRejected;

  return (
    <SignPageShell organization={q.organization}>
      {/* Header */}
      <div className="mb-6">
        <div className="kicker">Preventivo · Firma elettronica</div>
        <h1 className="t-h1 mt-1">{q.title ?? `Preventivo ${q.number ?? ''}`}</h1>
        <div className="mute mt-1">
          {q.organization?.name ?? 'Masamune'} → <strong>{q.client.legalName}</strong>
          {q.number && <> · <span className="mono">{q.number}</span></>}
        </div>
        <div className="mt-3 flex gap-2 flex-wrap">
          {isSigned && <Chip tone="ok" dot>Firmato — accettato</Chip>}
          {isRejected && <Chip tone="alert" dot>Rifiutato</Chip>}
          {isActionable && q.validUntil && <Chip tone="warn">Valido fino al {fmtDate(q.validUntil)}</Chip>}
        </div>
      </div>

      {/* Intro message */}
      {q.introMessage && (
        <div className="card mb-5">
          <div className="card-bd whitespace-pre-wrap leading-[1.6]">{q.introMessage}</div>
        </div>
      )}

      {/* Voci */}
      <div className="card mb-5">
        <div className="card-hd"><div className="t-h3">Voci della proposta</div></div>
        <table className="tbl">
          <thead><tr><th>Descrizione</th><th className="num">Q.tà</th><th className="num">€ unit</th><th className="num">IVA</th><th className="num">Totale</th></tr></thead>
          <tbody>
            {q.lines.map((l, i) => (
              <tr key={i}>
                <td>{l.description}</td>
                <td className="num mono">{l.quantity}</td>
                <td className="num mono">€ {fmtEur(l.unitPrice)}</td>
                <td className="num mono mute">{l.vatRate}%</td>
                <td className="num mono font-semibold">€ {fmtEur(l.quantity * l.unitPrice * (1 + l.vatRate / 100))}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="card-bd border-t border-line">
          <div className="grid grid-cols-3 gap-3 max-w-[480px] ml-auto">
            <div className="text-right"><div className="t-micro">Imponibile</div><div className="mono">€ {fmtEur(q.subtotal)}</div></div>
            <div className="text-right"><div className="t-micro">IVA</div><div className="mono mute">€ {fmtEur(q.vat)}</div></div>
            <div className="text-right"><div className="t-micro">Totale</div><div className="mono text-[22px] font-bold">€ {fmtEur(q.total)}</div></div>
          </div>
        </div>
      </div>

      {/* T&C */}
      {q.termsAndConditions && (
        <div className="card mb-5">
          <div className="card-hd"><div className="t-h3">Termini e condizioni</div></div>
          <div className="card-bd text-[13px] whitespace-pre-wrap font-mono leading-[1.55] mute" style={{ maxHeight: 280, overflowY: 'auto' }}>
            {q.termsAndConditions}
          </div>
        </div>
      )}

      {/* Receipt */}
      {receipt && (
        <div className="card mb-5" style={{ borderColor: 'var(--ok)' }}>
          <div className="card-hd flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-ok grid place-items-center text-white">
              <Icon name="check" size={14} />
            </div>
            <div>
              <div className="t-h3">Preventivo firmato</div>
              <div className="mute text-[12px]">Conferma inviata anche al fornitore — puoi chiudere questa pagina.</div>
            </div>
          </div>
          <div className="card-bd text-[13px]">
            <div className="grid gap-2" style={{ gridTemplateColumns: '130px 1fr' }}>
              <div className="mute">Firmato il</div><div className="mono">{new Date(receipt.timestamp).toLocaleString('it-IT')}</div>
              <div className="mute">Hash documento</div><div className="mono text-[12px] break-all">{receipt.documentHash}</div>
            </div>
            <div className="mt-3 text-[12px] mute leading-[1.5]">
              Questa è una firma elettronica semplice (SES) ai sensi del Regolamento eIDAS (UE n. 910/2014).
              Hash, IP, timestamp e immagine firma sono conservati come prova legale di accettazione.
            </div>
          </div>
        </div>
      )}

      {/* Action: firma o rifiuta */}
      {isActionable && !showSignBox && !showRejectBox && (
        <div className="card mb-5">
          <div className="card-bd py-6 text-center">
            <h2 className="text-[20px] font-semibold mb-2">Pronto a firmare?</h2>
            <p className="mute mb-5 max-w-[520px] mx-auto">
              Firmando dichiari di accettare la proposta e i relativi termini.
              È una <strong>firma elettronica semplice</strong> ai sensi del Regolamento eIDAS — gratuita e legalmente valida.
            </p>
            <div className="flex gap-2 justify-center flex-wrap">
              <button className="btn btn-primary" onClick={() => setShowSignBox(true)}>
                <Icon name="check" size={12} /> Firma il preventivo
              </button>
              <button className="btn" onClick={() => setShowRejectBox(true)}>
                <Icon name="x" size={12} /> Rifiuta
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signature box */}
      {showSignBox && (
        <div className="card mb-5">
          <div className="card-hd"><div className="t-h3">Firma qui sotto</div></div>
          <div className="card-bd space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="field-label">
                  Nome e cognome
                  <Helper text="Sarà associato alla tua firma come prova legale di chi ha accettato." />
                </label>
                <input className="inp" placeholder="Es. Mario Rossi" value={signerName} onChange={(e) => setSignerName(e.target.value)} autoFocus />
              </div>
              <div>
                <label className="field-label">Email (opz)</label>
                <input className="inp" type="email" placeholder="mario@example.com" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)} />
              </div>
            </div>

            <div>
              <label className="field-label flex items-center justify-between">
                <span>Firma (con mouse, trackpad o touch)</span>
                <button type="button" className="btn btn-sm btn-ghost" onClick={clearCanvas}>
                  <Icon name="x" size={10} /> Pulisci
                </button>
              </label>
              <canvas
                ref={canvasRef}
                className="w-full border border-line rounded-1"
                style={{ height: 160, background: '#fff', touchAction: 'none', cursor: 'crosshair' }}
              />
            </div>

            <label className="flex items-start gap-2 text-[13px] cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />
              <span>
                Dichiaro di aver letto e accettato la proposta commerciale, gli importi indicati e i termini e condizioni di cui sopra.
                <Helper text="La spunta è il consenso esplicito richiesto per la firma elettronica semplice. Senza non possiamo procedere." />
              </span>
            </label>

            <div className="flex gap-2 pt-2">
              <button className="btn" onClick={() => setShowSignBox(false)}>Annulla</button>
              <div className="spacer" />
              <button className="btn btn-primary" disabled={signing} onClick={handleSign}>
                <Icon name="check" size={12} /> {signing ? 'Firma in corso…' : 'Firma e accetta'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject box */}
      {showRejectBox && (
        <div className="card mb-5">
          <div className="card-hd"><div className="t-h3">Rifiuta preventivo</div></div>
          <div className="card-bd space-y-3">
            <p className="mute text-[13px]">Puoi indicare il motivo per aiutarci a capire (opzionale).</p>
            <textarea className="inp" rows={3} placeholder="Es. Preferisco un'altra soluzione, prezzo fuori budget, ecc." value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
            <div className="flex gap-2 pt-2">
              <button className="btn" onClick={() => setShowRejectBox(false)}>Annulla</button>
              <div className="spacer" />
              <button className="btn btn-danger" onClick={handleReject} disabled={signing}>
                Rifiuta definitivamente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Helpful footer info */}
      <div className="mt-8 text-[12px] mute text-center leading-[1.5]">
        Questa pagina è protetta da un token unguessable presente solo nel link che hai ricevuto.
        Per assistenza scrivi a chi ti ha inviato il preventivo.
      </div>
    </SignPageShell>
  );
}

// ════════════════════════════════════════════════════════════════════
function SignPageShell({ organization, children }: { organization?: { name: string; logoUrl: string | null } | null; children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper-2)', padding: '40px 20px' }}>
      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 rounded-1 bg-ink grid place-items-center px-3"><img src="/logos/m-white.svg" alt="Masamune" className="h-3.5 w-auto block" /></div>
          <div>
            <div className="font-bold tracking-[0.14em] uppercase text-[12px] mute">Powered by Masamune OS</div>
            <div className="font-semibold">{organization?.name ?? 'Masamune'}</div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}
